/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import TileLayer from 'ol/layer/Tile';
import { WMTS } from 'ol/source';
import type { Options as WMTSOptions } from 'ol/source/WMTS';
import { optionsFromCapabilities } from 'ol/source/WMTS';
import { useContext, useEffect, useMemo, useState } from 'react';
import type { LayerFoundation } from '@opengeoweb/webmap';
import { WMTSDimensionsFromDimensions } from './utils';
import { useQueryWMTSGetCapabilities } from '../../../hooks/useHooksGetCapabilities';
import MapContext from '../context/MapContext';
import {
  selectPreferredWMTSTileMatrixSet,
  useProjection,
} from '../utils/projections';

export interface WMTSLayerProps {
  getCapsURL: string;
  layerName: string;
  layerOptions: ConstructorParameters<typeof TileLayer>[0];
  layerProps?: LayerFoundation;
}

export const WMTSLayer: React.FC<WMTSLayerProps> = ({
  getCapsURL,
  layerName,
  layerOptions,
  layerProps,
}: WMTSLayerProps) => {
  const { map } = useContext(MapContext);
  const [layer, setLayer] = useState<TileLayer>();
  const [source, setSource] = useState<WMTS>();

  const {
    data: wmtsCaps,
    isLoading,
    isError,
  } = useQueryWMTSGetCapabilities(getCapsURL);

  const projectionName = useProjection();

  const wmtsLayer = useMemo((): WMTSOptions | null => {
    if (isLoading || isError || !wmtsCaps || !projectionName) {
      return null;
    }

    try {
      const capabilities = new WMTSCapabilities().read(wmtsCaps as string);
      const matrixSet = selectPreferredWMTSTileMatrixSet(
        capabilities,
        layerName,
        projectionName,
      );
      if (!matrixSet) {
        return null;
      }
      return optionsFromCapabilities(capabilities, {
        layer: layerName,
        matrixSet,
      });
    } catch (e) {
      console.error('Error parsing WMTS capabilities:', e);
      return null;
    }
  }, [isLoading, isError, wmtsCaps, layerName, projectionName]);

  useEffect(() => {
    if (!map || !wmtsLayer) {
      return (): void => {};
    }
    const source = new WMTS({
      ...wmtsLayer,
      ...(layerProps?.dimensions
        ? {
            dimensions: WMTSDimensionsFromDimensions(layerProps.dimensions),
          }
        : {}),
      crossOrigin: 'anonymous',
    });

    const layer = new TileLayer({ source });

    map.addLayer(layer);
    setLayer(layer);
    setSource(source);

    return (): void => {
      map.removeLayer(layer);
      setLayer(undefined);
      setSource(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, wmtsLayer]);

  useEffect(() => {
    if (layerProps?.dimensions) {
      source?.updateDimensions(
        WMTSDimensionsFromDimensions(layerProps.dimensions),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerProps?.dimensions]);

  useEffect(() => {
    if (!layerOptions || !layer) {
      return;
    }

    if (layerOptions.opacity !== undefined && layerOptions.opacity !== null) {
      layer.setOpacity(layerOptions.opacity);
    }
    if (layerOptions.zIndex !== undefined && layerOptions.zIndex !== null) {
      layer.setZIndex(layerOptions.zIndex);
    }
  }, [layer, layerOptions]);

  useEffect(() => {
    if (!layerProps || !layer) {
      return;
    }
    layer.setVisible(!!layerProps.enabled);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer, layerProps?.enabled]);

  return null;
};
